import { Button, Menu, MenuItem, Select, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { endOfMonth, endOfWeek, format, isValid, startOfMonth, startOfWeek, subDays, subMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import { surfaceSx } from '../../styles/tokens';

type DateRange = [string, string];

type QuickKey = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | '';

export interface DateRangeToolbarProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  direction?: 'row' | 'column';
  labels?: {
    quick?: string;
    month?: string;
    custom?: string;
    from?: string;
    to?: string;
    apply?: string;
  };
}

/**
 * Reusable toolbar pro výběr období:
 * - Select "Rychlé období" s presety
 * - Měsíční DatePicker (views=['year','month'])
 * - Dropdown "Vlastní" s DatePicker "od–do" + Použít
 */
export const DateRangeToolbar = ({
  value,
  onChange,
  weekStartsOn = 1,
  direction = 'row',
  labels = {
    quick: 'Rychlé období',
    month: 'Měsíc',
    custom: 'Vlastní datum',
    from: 'Datum od',
    to: 'Datum do',
    apply: 'Použít'
  }
}: DateRangeToolbarProps) => {
  const [quick, setQuick] = useState<QuickKey>('');
  const [customAnchor, setCustomAnchor] = useState<HTMLElement | null>(null);

  const [tmpFrom, setTmpFrom] = useState<Date | null>(value[0] ? new Date(value[0]) : null);
  const [tmpTo, setTmpTo] = useState<Date | null>(value[1] ? new Date(value[1]) : null);

  const toISO = (d: Date) => format(d, 'yyyy-MM-dd');

  const presets = useMemo(() => {
    const today = new Date();
    const monWeekOpts = { weekStartsOn };

    const todayStart = today;
    const todayEnd = today;

    const y = subDays(today, 1);

    const thisWeekStart = startOfWeek(today, monWeekOpts);
    const thisWeekEnd = endOfWeek(today, monWeekOpts);

    const lastWeekEnd = subDays(thisWeekStart, 1);
    const lastWeekStart = startOfWeek(lastWeekEnd, monWeekOpts);

    const thisMonthStart = startOfMonth(today);
    const thisMonthEnd = endOfMonth(today);

    const lastMonthDate = subMonths(today, 1);
    const lastMonthStart = startOfMonth(lastMonthDate);
    const lastMonthEnd = endOfMonth(lastMonthDate);

    return {
      today: [toISO(todayStart), toISO(todayEnd)] as DateRange,
      yesterday: [toISO(y), toISO(y)] as DateRange,
      thisWeek: [toISO(thisWeekStart), toISO(thisWeekEnd)] as DateRange,
      lastWeek: [toISO(lastWeekStart), toISO(lastWeekEnd)] as DateRange,
      thisMonth: [toISO(thisMonthStart), toISO(thisMonthEnd)] as DateRange,
      lastMonth: [toISO(lastMonthStart), toISO(lastMonthEnd)] as DateRange
    };
  }, [weekStartsOn]);

  const handleQuickChange = (key: QuickKey) => {
    setQuick(key);
    if (!key) return;
    const map: Record<Exclude<QuickKey, ''>, DateRange> = {
      today: presets.today,
      yesterday: presets.yesterday,
      thisWeek: presets.thisWeek,
      lastWeek: presets.lastWeek,
      thisMonth: presets.thisMonth,
      lastMonth: presets.lastMonth
    };
    onChange(map[key]);
    // zároveň aktualizuj lokální tmp hodnoty (ať menu ukazuje správné)
    const [f, t] = map[key];
    setTmpFrom(new Date(f));
    setTmpTo(new Date(t));
  };

  const handleMonthChange = (newMonth: Date | null) => {
    if (!newMonth || !isValid(newMonth)) return;
    const from = startOfMonth(newMonth);
    const to = endOfMonth(newMonth);
    const range: DateRange = [format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd')];
    onChange(range);
    setQuick('');
    setTmpFrom(from);
    setTmpTo(to);
  };

  const openCustom = (e: React.MouseEvent<HTMLElement>) => {
    setCustomAnchor(e.currentTarget);
    setTmpFrom(value[0] ? new Date(value[0]) : null);
    setTmpTo(value[1] ? new Date(value[1]) : null);
  };

  const closeCustom = () => setCustomAnchor(null);

  const applyCustom = () => {
    if (tmpFrom && tmpTo) {
      const range: DateRange = [format(tmpFrom, 'yyyy-MM-dd'), format(tmpTo, 'yyyy-MM-dd')];
      onChange(range);
      setQuick('');
      closeCustom();
    }
  };

  const quickItems: Array<{ key: Exclude<QuickKey, ''>; label: string }> = [
    { key: 'today', label: 'Dnes' },
    { key: 'yesterday', label: 'Včera' },
    { key: 'thisWeek', label: 'Tento týden' },
    { key: 'lastWeek', label: 'Minulý týden' },
    { key: 'thisMonth', label: 'Tento měsíc' },
    { key: 'lastMonth', label: 'Minulý měsíc' }
  ];

  return (
    <Stack direction={{ xs: 'column', md: direction }} flexWrap="wrap" gap={2} sx={{ ...surfaceSx }} p={2}>
      {/* Rychlé období */}
      <Stack direction="row" gap={2} alignItems="center">
        <Select
          size="small"
          displayEmpty
          value={quick}
          onChange={(e) => handleQuickChange(e.target.value as QuickKey)}
          renderValue={(v) => {
            const found = quickItems.find((i) => i.key === v);
            return found ? found.label : 'Vyberte období…';
          }}
          sx={{ minWidth: 200, width: { xs: '100%', md: 'fit-content' } }}
        >
          {quickItems.map((i) => (
            <MenuItem key={i.key} value={i.key}>
              {i.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {/* Rychlá volba: Měsíc */}
      <Stack direction="row" gap={2} alignItems="center">
        <DatePicker
          label={labels.month}
          views={['year', 'month']}
          openTo="month"
          format="LLLL yyyy"
          value={new Date(value[0])}
          onChange={handleMonthChange}
          slotProps={{ textField: { size: 'small' } }}
          sx={{ width: { xs: '100%', md: 'fit-content' }, minWidth: { md: 200 } }}
        />
      </Stack>

      {/* Vlastní: dropdown s od–do */}
      <Stack direction="row" gap={2} alignItems="center">
        <Button variant="outlined" onClick={openCustom}>
          {labels.custom}
        </Button>
        <Menu
          anchorEl={customAnchor}
          open={Boolean(customAnchor)}
          onClose={closeCustom}
          slotProps={{ paper: { sx: { p: 2 } } }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <DatePicker label={labels.from} format="yyyy-MM-dd" value={tmpFrom} onChange={(d) => setTmpFrom(d)} />
            <DatePicker label={labels.to} format="yyyy-MM-dd" value={tmpTo} onChange={(d) => setTmpTo(d)} />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={applyCustom}
              disabled={!tmpFrom || !tmpTo || !isValid(tmpFrom) || !isValid(tmpTo)}
            >
              {labels.apply}
            </Button>
          </Stack>
        </Menu>
      </Stack>
    </Stack>
  );
};
